/**
 *
 * @module playbackTelemetryDispatcher
 *
 */

import uuid from 'uuid';
import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Internal from './../internal/entry';
import PlaybackEventListener from './playbackEventListener';
import PlaybackTelemetryConfiguration from './playbackTelemetryConfiguration';
import PlaybackMetricsProvider from './playbackMetricsProvider';

const TelemetryManager = Internal.Telemetry.TelemetryManager;
const StreamSampleTelemetryEvent = Internal.Telemetry.StreamSampleTelemetryEvent;

const ErrorReason = Services.Exception.ErrorReason;
const ExceptionReference = Services.Exception.ExceptionReference;
const InvalidArgumentException = Services.Exception.InvalidArgumentException;
const ServiceException = Services.Exception.ServiceException;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class PlaybackTelemetryDispatcher
 * @extends {PlaybackEventListener}
 * @desc Handles stream sample and other player telemetry events
 * @since 2.0.0
 *
 */
export default class PlaybackTelemetryDispatcher extends PlaybackEventListener {

    /**
     *
     * @param {PlaybackTelemetryConfiguration} playbackTelemetryConfiguration
     * @param {SDK.Internal.Telemetry.TelemetryManager} telemetryManager
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(playbackTelemetryConfiguration, telemetryManager, logger = Logging) {

        const errorMessage = {
            ctor: 'PlaybackTelemetryDispatcher(playbackTelemetryConfiguration, telemetryManager, logger)',
            get playbackTelemetryConfiguration() {
                return `${this.ctor} needs PlaybackTelemetryConfiguration instance`;
            },
            get telemetryManager() {
                return `${this.ctor} needs TelemetryManager instance`;
            }
        };

        instanceStrict(playbackTelemetryConfiguration, PlaybackTelemetryConfiguration, errorMessage.playbackTelemetryConfiguration, InvalidArgumentException); // eslint-disable-line
        instanceStrict(telemetryManager, TelemetryManager, errorMessage.telemetryManager, InvalidArgumentException);

        super();

        /**
         *
         * @access private
         * @type {PlaybackTelemetryConfiguration}
         *
         */
        this.config = playbackTelemetryConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Internal.Telemetry.TelemetryManager}
         *
         */
        this.manager = telemetryManager;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {Object}
         *
         */
        this.serverData = {};

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.sessionId = null;

        /**
         *
         * @access private
         * @type {Number}
         *
         */
        this.interval = this.config.streamSampleInterval;

        /**
         *
         * @access private
         * @type {Number}
         *
         */
        this.intervalId = -1;

        /**
         *
         * @access private
         * @type {Boolean}
         *
         */
        this.isInitialized = false;

        /**
         *
         * @access private
         * @type {Boolean}
         *
         */
        this.isReleased = false;

        this.logger.log(PlaybackTelemetryDispatcher.name, 'Created.');

    }

    /**
     *
     * @access private
     * @param {PlaybackMetricsProvider} playbackMetricsProvider
     * @param {Object} serverData
     * @desc Initializes the dispatcher with a provider and telemetry data.
     *
     */
    init(playbackMetricsProvider, serverData) {

        const errorMessage = {
            method: 'PlaybackTelemetryDispatcher.init(playbackMetricsProvider, serverData)',
            get playbackMetricsProvider() {
                return `${this.method} needs PlaybackMetricsProvider instance`;
            }
        };

        instanceStrict(playbackMetricsProvider, PlaybackMetricsProvider, errorMessage.playbackMetricsProvider, InvalidArgumentException); // eslint-disable-line

        const logger = this.logger;
        const isReleased = this.isReleased;

        if (Check.emptyObject(serverData)) {
            return;
        }

        if (isReleased) {

            const reasons = [new ErrorReason('', 'The dispatcher has been released and cannot be reused.')];
            const exceptionData = ExceptionReference.common.invalidState;

            throw new ServiceException({ reasons, exceptionData });

        }

        playbackMetricsProvider.addListener(this).then(() => {

            this.serverData = serverData;
            this.sessionId = uuid.v4();
            this.isInitialized = true;

            logger.info(PlaybackTelemetryDispatcher.name, 'Initialized.');

        }).catch((ex) => {

            logger.error(PlaybackTelemetryDispatcher.name, ex);

        });

    }

    /**
     *
     * @access public
     * @param {PlaybackMetricsProvider} playbackMetricsProvider
     * @desc Cleans up the dispatcher and prevents further use.
     * @returns {Promise<Void>}
     *
     */
    release(playbackMetricsProvider) {

        const errorMessage = {
            method: 'PlaybackTelemetryDispatcher.release(playbackMetricsProvider)',
            get playbackMetricsProvider() {
                return `${this.method} needs PlaybackMetricsProvider instance`;
            }
        };

        instanceStrict(playbackMetricsProvider, PlaybackMetricsProvider, errorMessage.playbackMetricsProvider, InvalidArgumentException); // eslint-disable-line

        const logger = this.logger;

        logger.warn(PlaybackTelemetryDispatcher.name, 'Released.');

        if (this.isReleased) {
            return Promise.resolve();
        }

        return playbackMetricsProvider.removeListener(this).then(() => {

            this.clearStreamInterval();

            this.isInitialized = false;
            this.isReleased = true;

            return Promise.resolve();

        }).catch((ex) => {

            logger.error(PlaybackTelemetryDispatcher.name, ex);

            return Promise.reject(ex);

        });

    }

    /**
     *
     * @access public
     * @param {PlaybackMetricsProvider} playbackMetricsProvider
     * @returns {Promise<Void>}
     *
     */
    onPlay(playbackMetricsProvider) {

        const errorMessage = {
            method: 'PlaybackTelemetryDispatcher.onPlay(playbackMetricsProvider)',
            get playbackMetricsProvider() {
                return `${this.method} needs PlaybackMetricsProvider instance`;
            }
        };

        instanceStrict(playbackMetricsProvider, PlaybackMetricsProvider, errorMessage.playbackMetricsProvider, InvalidArgumentException); // eslint-disable-line

        if (!this.isInitialized) {
            return Promise.resolve();
        }

        return this.startRecordingSamples(playbackMetricsProvider);

    }

    /**
     *
     * @access public
     * @param {PlaybackMetricsProvider} playbackMetricsProvider
     * @returns {Promise<Void>}
     *
     */
    onPause(playbackMetricsProvider) {

        const errorMessage = {
            method: 'PlaybackTelemetryDispatcher.onPause(playbackMetricsProvider)',
            get playbackMetricsProvider() {
                return `${this.method} needs PlaybackMetricsProvider instance`;
            }
        };

        instanceStrict(playbackMetricsProvider, PlaybackMetricsProvider, errorMessage.playbackMetricsProvider, InvalidArgumentException); // eslint-disable-line

        if (!this.isInitialized) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {

            this.clearStreamInterval();

            return resolve();

        });

    }

    /**
     *
     * @access public
     * @param {PlaybackMetricsProvider} playbackMetricsProvider
     * @param {String} message
     * @returns {Promise<Void>}
     *
     */
    onError(playbackMetricsProvider, message) {

        const errorMessage = {
            method: 'PlaybackTelemetryDispatcher.onError(playbackMetricsProvider, message)',
            get playbackMetricsProvider() {
                return `${this.method} needs PlaybackMetricsProvider instance`;
            },
            get message() {
                return `${this.method}`;
            }
        };

        instanceStrict(playbackMetricsProvider, PlaybackMetricsProvider, errorMessage.playbackMetricsProvider, InvalidArgumentException); // eslint-disable-line

        if (!Check.string(message) || Check.emptyString(message)) {
            this.logger.warn(errorMessage.method, errorMessage.message);
        }

        return this.release(playbackMetricsProvider);

    }

    /**
     *
     * @access public
     * @param {PlaybackMetricsProvider} playbackMetricsProvider
     * @returns {Promise<Void>}
     *
     */
    onPlayedToCompletion(playbackMetricsProvider) {

        const errorMessage = {
            method: 'PlaybackTelemetryDispatcher.onPlayedToCompletion(playbackMetricsProvider)',
            get playbackMetricsProvider() {
                return `${this.method} needs PlaybackMetricsProvider instance`;
            }
        };

        instanceStrict(playbackMetricsProvider, PlaybackMetricsProvider, errorMessage.playbackMetricsProvider, InvalidArgumentException); // eslint-disable-line

        return this.onPause(playbackMetricsProvider);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {PlaybackMetricsProvider} playbackMetricsProvider
     * @returns {Promise<Void>}
     *
     */
    startRecordingSamples(playbackMetricsProvider) {

        return this.timedRecordStreamSample(playbackMetricsProvider);

    }

    /**
     *
     * @access private
     * @param {PlaybackMetricsProvider} playbackMetricsProvider
     * @returns {Promise<Void>}
     *
     */
    timedRecordStreamSample(playbackMetricsProvider) {

        const { isInitialized, interval } = this;

        if (!isInitialized) {
            return Promise.resolve();
        }

        return this.recordStreamSample(playbackMetricsProvider).then(() => {

            this.intervalId = setTimeout(() => {

                this.timedRecordStreamSample(playbackMetricsProvider);

            }, interval * 1000);

        });


    }

    /**
     *
     * @access private
     * @param {PlaybackMetricsProvider} playbackMetricsProvider
     * @returns {Promise<Void>}
     *
     */
    recordStreamSample(playbackMetricsProvider) {

        const { isInitialized, serverData } = this;

        if (!isInitialized) {
            return Promise.resolve();
        }

        return playbackMetricsProvider.getPlaybackMetrics().then((metrics) => {

            const playbackSessionId = this.sessionId;
            const playhead = metrics.currentPlayhead;
            const bitrate = metrics.currentBitrate;
            const streamSampleTelemetryEvent = new StreamSampleTelemetryEvent({
                playbackSessionId,
                playhead,
                bitrate,
                serverData
            });

            return this.manager.streamSampleBuffer.postEvent(streamSampleTelemetryEvent);

        });

    }

    /**
     *
     * @access private
     * @desc Clears the stream interval for sending telemetry events
     *
     */
    clearStreamInterval() {

        clearTimeout(this.intervalId);
        this.intervalId = -1;

    }

}



// WEBPACK FOOTER //
// ./src/media/playbackTelemetryDispatcher.js